import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { BoostButtons } from '../components/BoostButtons';
import { MomentumScore } from '../components/MomentumScore';
import { TrustBadge } from '../components/TrustBadge';
import { 
  ArrowLeft,
  ExternalLink,
  Github,
  Figma,
  Calendar,
  Users,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  Flag,
  Star
} from 'lucide-react';
import { projectAPI } from '../lib/api';
import { 
  formatRelativeTime, 
  formatCurrency, 
  formatNumber,
  getAvatarFallback,
  LEGAL_DISCLAIMER 
} from '../lib/utils';

export function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProject = async () => {
    try {
      const response = await projectAPI.get(id);
      setProject(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [id]);

  const handleBoostComplete = (updatedProject) => {
    if (updatedProject) {
      setProject(prev => ({ ...prev, ...updatedProject }));
    } else {
      fetchProject();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Project Not Found</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/discover')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }

  const defaultImage = 'https://images.pexels.com/photos/3912477/pexels-photo-3912477.jpeg';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back Navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hero Image */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-100">
              <img
                src={project.image_url || defaultImage}
                alt={project.title}
                className="w-full h-full object-cover"
              />
              {project.status === 'smart_pick' && (
                <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 font-medium">
                  <Star className="w-4 h-4" />
                  Smart Pick
                </div>
              )}
            </div>

            {/* Title & Meta */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="secondary" className="capitalize text-sm">
                  {project.category}
                </Badge>
                {project.tags?.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="project-title">
                {project.title}
              </h1>

              <p className="text-xl text-slate-600">
                {project.tagline}
              </p>

              {/* Creator Info */}
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={project.creator_picture} alt={project.creator_name} />
                  <AvatarFallback className="bg-orange-100 text-orange-700">
                    {getAvatarFallback(project.creator_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Link 
                    to={`/profile/${project.creator_id}`}
                    className="font-semibold text-slate-900 hover:text-orange-600 transition-colors"
                  >
                    {project.creator_name}
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="w-3 h-3" />
                    {formatRelativeTime(project.created_at)}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="prose prose-slate max-w-none">
              <h3 className="text-xl font-bold text-slate-900 mb-4">About this project</h3>
              <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                {project.description}
              </p>
            </div>

            {/* Links */}
            {(project.demo_url || project.github_url || project.figma_url) && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-900">Links</h3>
                <div className="flex flex-wrap gap-3">
                  {project.demo_url && (
                    <a
                      href={project.demo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 hover:border-orange-300 transition-colors"
                      data-testid="demo-link"
                    >
                      <ExternalLink className="w-4 h-4 text-orange-500" />
                      <span className="font-medium">Live Demo</span>
                    </a>
                  )}
                  {project.github_url && (
                    <a
                      href={project.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 hover:border-slate-400 transition-colors"
                      data-testid="github-link"
                    >
                      <Github className="w-4 h-4" />
                      <span className="font-medium">GitHub</span>
                    </a>
                  )}
                  {project.figma_url && (
                    <a
                      href={project.figma_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 hover:border-purple-300 transition-colors"
                    >
                      <Figma className="w-4 h-4 text-purple-500" />
                      <span className="font-medium">Figma</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Comments Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-slate-900">
                Comments ({project.comments?.length || 0})
              </h3>

              {project.comments?.length > 0 ? (
                <div className="space-y-4">
                  {project.comments.map((comment) => (
                    <CommentCard key={comment.comment_id} comment={comment} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-white rounded-xl border border-slate-200">
                  <MessageCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">No comments yet. Be the first!</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Momentum Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
              <MomentumScore
                score={project.momentum_score}
                freeScore={project.free_boost_score}
                timeScore={project.time_boost_score}
                moneyScore={project.money_boost_score}
                showBreakdown
              />

              <Separator className="my-6" />

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <StatItem 
                  icon={Users} 
                  value={formatNumber(project.unique_supporters)} 
                  label="Supporters" 
                />
                <StatItem 
                  icon={DollarSign} 
                  value={formatCurrency(project.total_money_raised)} 
                  label="Raised" 
                  highlight
                />
                <StatItem 
                  icon={Heart} 
                  value={formatNumber(project.total_likes)} 
                  label="Likes" 
                />
                <StatItem 
                  icon={MessageCircle} 
                  value={formatNumber(project.total_comments)} 
                  label="Comments" 
                />
              </div>

              <Separator className="my-6" />

              {/* Boost Buttons */}
              <BoostButtons
                project={project}
                userBoosts={project.user_boosts || []}
                onBoostComplete={handleBoostComplete}
              />

              {/* Disclaimer */}
              <div className="disclaimer-banner mt-6">
                {LEGAL_DISCLAIMER}
              </div>
            </div>

            {/* Top Supporters */}
            {project.top_supporters?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h4 className="font-bold text-slate-900 mb-4">Top Supporters</h4>
                <div className="space-y-3">
                  {project.top_supporters.slice(0, 5).map((supporter, index) => (
                    <div key={supporter._id} className="flex items-center gap-3">
                      <span className="w-6 text-center font-bold text-slate-400">
                        #{index + 1}
                      </span>
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={supporter.user_picture} />
                        <AvatarFallback className="text-xs">
                          {getAvatarFallback(supporter.user_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-sm font-medium truncate">
                        {supporter.user_name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {supporter.total_boosts} boosts
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatItem({ icon: Icon, value, label, highlight = false }) {
  return (
    <div className="text-center p-3 bg-slate-50 rounded-xl">
      <Icon className={`w-5 h-5 mx-auto mb-1 ${highlight ? 'text-green-600' : 'text-slate-400'}`} />
      <div className={`font-bold ${highlight ? 'text-green-600' : 'text-slate-900'}`}>
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function CommentCard({ comment }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-start gap-3">
        <Avatar className="w-8 h-8">
          <AvatarImage src={comment.user_picture} />
          <AvatarFallback className="text-xs">
            {getAvatarFallback(comment.user_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-slate-900">{comment.user_name}</span>
            {comment.is_review && (
              <Badge variant="secondary" className="text-xs">Review</Badge>
            )}
            {comment.is_feedback && !comment.is_review && (
              <Badge variant="outline" className="text-xs">Feedback</Badge>
            )}
            <span className="text-xs text-slate-400 ml-auto">
              {formatRelativeTime(comment.created_at)}
            </span>
          </div>
          <p className="text-slate-700 text-sm whitespace-pre-wrap">
            {comment.content}
          </p>
          
          {comment.is_review && (comment.strengths?.length > 0 || comment.risks?.length > 0) && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {comment.strengths?.length > 0 && (
                <div className="p-2 bg-green-50 rounded-lg">
                  <span className="text-xs font-medium text-green-700">Strengths:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {comment.strengths.map((s, i) => (
                      <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {comment.risks?.length > 0 && (
                <div className="p-2 bg-red-50 rounded-lg">
                  <span className="text-xs font-medium text-red-700">Risks:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {comment.risks.map((r, i) => (
                      <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
